import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';

import {
    NestedNode,
    NestedNodeSelectInsightCanSelectFn,
    NestedNodeSelectInsightChangeEvent,
    NestedNodeState,
} from './nested-node-select.insight.model';

@Component({
    selector: 'nested-node-select-insight',
    templateUrl: './nested-node-select.insight.component.html',
})
export class NestedNodeSelectInsightComponent
    implements OnChanges, OnInit, OnDestroy
{
    @Input() nodes: NestedNode[];
    @Input() selection: NestedNode[];
    @Input() standAloneParent: boolean;
    @Input() canSelect: NestedNodeSelectInsightCanSelectFn;
    @Output() change: EventEmitter<NestedNodeSelectInsightChangeEvent>;

    localState: {
        atLeastOneHasChild: boolean;
        states: { [key: string]: NestedNodeState };
    };

    constructor() {
        this.change = new EventEmitter();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes.nodes != null ||
            changes.selection != null ||
            changes.standAloneParent != null ||
            changes.canSelect != null
        ) {
            this._setValues();
        }
    }

    ngOnInit(): void {
        this._setValues();
    }

    ngOnDestroy(): void {
        this.change.complete();
    }

    _onClick(event: MouseEvent, node: NestedNode, key: string): void {
        event.stopPropagation();
        const canSelect =
            this.canSelect?.(node, this.localState.states[key].checked) ?? true;
        if (!canSelect) {
            event.preventDefault();
        }
    }

    _onChange(event: Event, key: string): void {
        event.stopPropagation();
        const target = event.target as HTMLInputElement;
        const states = { ...this.localState.states };
        states[key].checked = target.checked;
        !this.standAloneParent &&
            this._findChildren(key).forEach(
                (e) => (e.checked = states[key].checked),
            );
        const parent = this._findParent(key);
        if (parent) {
            parent.checked =
                (!this.standAloneParent &&
                    this._checkSelected(parent.key, 'any')) ||
                (this.standAloneParent && parent.checked);
        }
        const nodesFlatten = this.nodes
            .reduce((p, c) => p.concat(c, c.children), [])
            .filter((e) => !!e);
        this.change?.emit({
            selected: Object.values(this.localState.states)
                .filter((e) => e.checked)
                .map((e) => nodesFlatten.find((ee) => ee.id === e.id))
                .filter((e) => !!e),
        });
        this.localState = { ...this.localState, states };
    }

    _onExpandClick(key: string): void {
        const states = { ...this.localState.states };
        states[key].expanded = !states[key].expanded;
        this.localState = { ...this.localState, states };
    }

    private _findParent(key: string): NestedNodeState {
        const parts = key.split('-');
        const lookup = parts.slice(0, parts.length - 1).join('-');
        return Object.keys(this.localState.states)
            .filter((e) => e === lookup)
            .map((e) => this.localState.states[e])
            .find((e) => !!e);
    }

    private _findChildren(parentKey: string): NestedNodeState[] {
        return Object.keys(this.localState.states)
            .filter((e) => e.startsWith(`${parentKey}-`))
            .map((e) => this.localState.states[e]);
    }

    private _checkSelected(parentKey: string, which: 'some' | 'any'): boolean {
        const vals = this._findChildren(parentKey);
        const match = vals.filter((e: NestedNodeState) => e.checked);
        const some = match.length > 0 && match.length < vals.length;
        const all = match.length === vals.length;
        return which === 'some' ? some : which === 'any' ? some || all : false;
    }

    private _setValues(): void {
        const states: { [key: string]: NestedNodeState } = {};
        const mapNodes = (
            nodes: NestedNode[],
            parentKey: string = undefined,
            depth: number = 0,
        ): void => {
            if (nodes == null || depth > 1) return null;
            nodes.forEach((e, i) => {
                const key = `${parentKey}-${i}`;
                (e as any).key = key; // fixme
                (e as any).rand = Math.random(); // fixme
                (e as any).depth = depth; // fixme
                states[key] = {
                    key,
                    id: e.id,
                    label: e.label,
                    checked:
                        e.checked ||
                        this.selection?.some((ee) => ee.id === e.id),
                    disabled: e.disabled,
                    expanded: false,
                };
                if (parentKey) {
                    states[parentKey].expanded =
                        (this.standAloneParent && states[parentKey].checked) ||
                        states[parentKey].expanded ||
                        states[key].checked;
                }
                mapNodes(e.children, key, depth + 1);
            });
        };
        mapNodes(this.nodes);
        const atLeastOneHasChild = this.nodes.some(
            (e) => e.children?.length > 0,
        );
        this.localState = { atLeastOneHasChild, states };
    }
}
